/**
 * `@puffin-core/mfa-ui/send-failure` — the ONE judgement of why a one-time code could not be sent, for every surface
 * that sends one: mfa-ui's send step (`useOtpSendFlow`) and palm-native's sign-in (`useMfaVerify`).
 *
 * It decides the REASON, never the words: each surface maps a reason to its own catalog leaf and presents its own
 * parameters (web shows the START number as PortPuffin sent it, E.164; native formats it). The two copies this
 * replaced judged identically when measured (JSDOC-PROP-CONTRACT-001, 2026-10-28), and both read the route's
 * `MFA_METHOD_NOT_ENROLLED` as "couldn't send, try again", which retrying can never cure.
 *
 * DOM-free and dependency-free: palm-native imports this file alone, so mfa-ui's React, router and page-shell graph
 * never reaches it (`sendFailure.test.js` and palm-native's `useMfaVerify.sendFailureReach.test.js` hold that).
 *
 * @module @puffin-core/mfa-ui/send-failure
 */

/** The send-otp refusal for a number that texted STOP to the sending number (`createMfaOtpRouter`). */
const SMS_OPTED_OUT = 'SMS_OPTED_OUT';

/**
 * The send-otp refusal for a number on the sending COMPANY's own opt-out list — a platform-number code (owner ruling
 * Q-SMS-F3, "Also check company opt-outs"). START to the platform number lifts nothing there, so none is named.
 */
const SMS_OPTED_OUT_BY_COMPANY = 'SMS_OPTED_OUT_BY_COMPANY';

/** The send-otp refusal for a method the person never set up (Q-EE-35; auth-routes `mfaOtp`, 400). */
const MFA_METHOD_NOT_ENROLLED = 'MFA_METHOD_NOT_ENROLLED';

/**
 * A send inside the server's pace (auth-routes `refuseCodeBeforeItsPace`, D1 errorCode #2b): 429 with the wait the
 * server will hold, `details.retryAfter`, in whole seconds.
 */
const RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED';

/**
 * Why a code could not be sent. Each surface words each one in its own catalog.
 *
 * @type {Readonly<{OPTED_OUT: 'optedOut', OPTED_OUT_BY_COMPANY: 'optedOutByCompany',
 *   CHALLENGE_INVALID: 'challengeInvalid', CHALLENGE_EXPIRED: 'challengeExpired', NOT_ENROLLED: 'notEnrolled',
 *   TOO_SOON: 'tooSoon', FAILED: 'failed'}>}
 */
export const SEND_FAILURE_REASONS = Object.freeze({
  OPTED_OUT: 'optedOut',
  OPTED_OUT_BY_COMPANY: 'optedOutByCompany',
  CHALLENGE_INVALID: 'challengeInvalid',
  CHALLENGE_EXPIRED: 'challengeExpired',
  NOT_ENROLLED: 'notEnrolled',
  TOO_SOON: 'tooSoon',
  FAILED: 'failed',
});

/**
 * The send-otp refusals that end a sign-in's challenge (auth-routes `mfaOtp`, the ONE MFA verify vocabulary): the code
 * cannot be sent, so the person signs in again.
 */
const CHALLENGE_ENDED = Object.freeze({
  MFA_CHALLENGE_INVALID: SEND_FAILURE_REASONS.CHALLENGE_INVALID,
  MFA_SESSION_EXPIRED: SEND_FAILURE_REASONS.CHALLENGE_EXPIRED,
});

/**
 * The wait a paced refusal names, or null when it names none a client can prove (a positive whole number).
 *
 * @param {{errorCode?: string, errorDetails?: Object}|null|undefined} refusal
 * @returns {?number}
 */
export function provenRetryAfterOf(refusal) {
  if (refusal?.errorCode !== RATE_LIMIT_EXCEEDED) return null;
  const seconds = refusal.errorDetails?.retryAfter;
  return Number.isInteger(seconds) && seconds > 0 ? seconds : null;
}

/**
 * Why a send failed, from the server's refusal (its `errorCode` / `errorDetails`): an auth-client error carries them
 * itself, an api-client refusal is a failure's `cause`. Never from a thrown message.
 *
 * @param {{errorCode?: string, errorDetails?: Object}|null|undefined} refusal
 * @returns {{reason: string, params: Object}} a frozen `{ reason, params }`: `optedOut` names `number` (the START
 *   number as the server sent it, or null), `tooSoon` names `seconds`; every other reason has no params
 */
export function sendFailureOf(refusal) {
  const code = refusal?.errorCode;
  const said = (reason, params = {}) => Object.freeze({ reason, params: Object.freeze(params) });
  if (code === SMS_OPTED_OUT) {
    return said(SEND_FAILURE_REASONS.OPTED_OUT, {
      number: refusal.errorDetails?.replyStartTo || null,
    });
  }
  if (code === SMS_OPTED_OUT_BY_COMPANY) return said(SEND_FAILURE_REASONS.OPTED_OUT_BY_COMPANY);
  if (Object.hasOwn(CHALLENGE_ENDED, code ?? '')) return said(CHALLENGE_ENDED[code]);
  if (code === MFA_METHOD_NOT_ENROLLED) return said(SEND_FAILURE_REASONS.NOT_ENROLLED);
  const seconds = provenRetryAfterOf(refusal);
  if (seconds !== null) return said(SEND_FAILURE_REASONS.TOO_SOON, { seconds });
  return said(SEND_FAILURE_REASONS.FAILED);
}
